/* ============================================================
   UGC Video Carousel
   ============================================================ */

var UGCVideoCarousel = (function () {
  'use strict';

  function init(opts) {
    var carouselEl = document.getElementById(opts.carouselId);
    var modalEl = document.getElementById(opts.modalId);
    var videos = opts.videos || [];

    if (!carouselEl || !modalEl || !videos.length) return;

    // Move modal to document.body so it escapes any ancestor stacking context
    // (e.g. position:sticky on col-start-2 would otherwise cap its z-index)
    document.body.appendChild(modalEl);

    var videoEl = modalEl.querySelector('.ugc-modal__video');
    var currentIndex = 0;
    var isMuted = true;
    var playPauseTimer = null;

    // Carousel autoplay state
    var carouselTrack = carouselEl.querySelector('.ugc-carousel__track');
    var carouselThumbVideos = carouselEl.querySelectorAll('.ugc-carousel__thumb-video');
    var autoplayTimer = null;
    var autoplayIndex = 0;
    var autoplayRunning = false;
    var isHovering = false;

    // Measure the gap between the track's left edge and its first item once at init
    // (scrollLeft is 0, layout is stable). This equals the track's effective left padding
    // whether it comes from CSS padding, a parent container, or anything else.
    var firstTrackChild = carouselTrack && carouselTrack.firstElementChild;
    var initialTrackPad = firstTrackChild
      ? firstTrackChild.getBoundingClientRect().left - carouselTrack.getBoundingClientRect().left
      : 0;

    // Cache DOM references
    var closeBtn    = modalEl.querySelector('.ugc-modal__close');
    var muteBtn     = modalEl.querySelector('.ugc-modal__mute');
    var backdrop    = modalEl.querySelector('.ugc-modal__backdrop');
    var playPauseEl = modalEl.querySelector('.ugc-modal__play-pause');
    var videoArea   = modalEl.querySelector('.ugc-modal__video-area');
    var prevBtn     = modalEl.querySelector('.ugc-modal__nav--prev');
    var nextBtn     = modalEl.querySelector('.ugc-modal__nav--next');
    var reviewerEl     = modalEl.querySelector('.ugc-modal__reviewer');
    var nameEl         = modalEl.querySelector('.ugc-modal__reviewer-name');
    var handleEl       = modalEl.querySelector('.ugc-modal__reviewer-handle');
    var starsEl        = modalEl.querySelector('.ugc-modal__stars');
    var snippetWrapEl  = modalEl.querySelector('.ugc-modal__snippet-wrap');
    var snippetEl      = modalEl.querySelector('.ugc-modal__snippet');
    var snippetToggle  = modalEl.querySelector('.ugc-modal__snippet-toggle');
    var atcBtn         = modalEl.querySelector('.ugc-modal__atc');
    var atcBtnLabel = atcBtn ? atcBtn.textContent.trim() : '';

    // Dynamic product bar refs — only used when opts.dynamicProductBar is true (section mode).
    // PDP never sets this flag so the PDP code path is completely unaffected.
    var dynProductBar   = opts.dynamicProductBar ? modalEl.querySelector('.ugc-modal__product-bar')    : null;
    var dynProductLink  = opts.dynamicProductBar ? modalEl.querySelector('.ugc-modal__product-link')   : null;
    var dynProductImg   = opts.dynamicProductBar ? modalEl.querySelector('.ugc-modal__product-img')    : null;
    var dynProductName  = opts.dynamicProductBar ? modalEl.querySelector('.ugc-modal__product-name')   : null;
    var dynProductPrice = opts.dynamicProductBar ? modalEl.querySelector('.ugc-modal__product-price')  : null;

    // Measure scrollbar width once so body padding can compensate on scroll-lock
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--ugc-scrollbar-width', scrollbarWidth + 'px');

    // ----------------------------------------------------------------
    // Carousel item click → open modal
    // ----------------------------------------------------------------
    carouselEl.querySelectorAll('[data-ugc-index]').forEach(function (btn) {
      // In section mode the button sits inside a .ugc-carousel__item-wrap that also
      // contains the caption, so attach the click there so the whole card is tappable.
      // In PDP mode there is no wrap, so fall back to the button itself.
      var clickTarget = btn.closest('.ugc-carousel__item-wrap') || btn;
      clickTarget.addEventListener('click', function () {
        openModal(parseInt(btn.dataset.ugcIndex, 10));
      });

      btn.addEventListener('mouseenter', function () {
        var idx = parseInt(this.dataset.ugcIndex, 10);
        isHovering = true;
        clearTimeout(autoplayTimer);
        carouselThumbVideos.forEach(function (v, i) {
          if (i !== idx) { v.pause(); v.currentTime = 0; }
        });
        if (carouselThumbVideos[idx]) {
          carouselThumbVideos[idx].play().catch(function () {});
        }
        autoplayIndex = idx;
      });
    });

    if (carouselTrack) {
      carouselTrack.addEventListener('mouseleave', function () {
        isHovering = false;
        if (autoplayRunning) {
          clearTimeout(autoplayTimer);
          autoplayTimer = setTimeout(function () {
            playCarouselThumb((autoplayIndex + 1) % carouselThumbVideos.length);
          }, 5000);
        }
      });
    }

    // Hide/show play icon based on actual video play state
    carouselThumbVideos.forEach(function (v) {
      var btn = v.closest('[data-ugc-index]');
      if (!btn) return;
      v.addEventListener('play', function () { btn.classList.add('is-playing'); });
      v.addEventListener('pause', function () { btn.classList.remove('is-playing'); });
    });

    // ----------------------------------------------------------------
    // Close
    // ----------------------------------------------------------------
    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (e) {
      if (modalEl.getAttribute('aria-hidden') === 'true') return;
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key === 'ArrowUp')   navigate(-1);
      if (e.key === 'ArrowDown') navigate(1);
    });

    // ----------------------------------------------------------------
    // Mute toggle (stop propagation so video click doesn't also fire)
    // ----------------------------------------------------------------
    muteBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleMute();
    });

    // ----------------------------------------------------------------
    // Snippet see more / see less toggle
    // ----------------------------------------------------------------
    if (snippetToggle) {
      snippetToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var expanded = snippetEl.classList.toggle('is-expanded');
        snippetToggle.textContent = expanded ? 'See less' : 'See more';
      });
    }

    // ----------------------------------------------------------------
    // Play / pause on video area click
    // ----------------------------------------------------------------
    videoArea.addEventListener('click', function (e) {
      // Ignore clicks on overlaid buttons
      if (e.target.closest('.ugc-modal__nav') ||
          e.target.closest('.ugc-modal__btn')) return;
      togglePlayPause();
    });

    // ----------------------------------------------------------------
    // Navigation buttons
    // ----------------------------------------------------------------
    prevBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigate(-1);
    });
    nextBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigate(1);
    });

    // ----------------------------------------------------------------
    // Swipe up / down on mobile
    // ----------------------------------------------------------------
    var touchStartY = 0;
    var touchStartX = 0;

    videoArea.addEventListener('touchstart', function (e) {
      touchStartY = e.changedTouches[0].clientY;
      touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });

    videoArea.addEventListener('touchend', function (e) {
      var dy = touchStartY - e.changedTouches[0].clientY;
      var dx = Math.abs(touchStartX - e.changedTouches[0].clientX);
      // Only treat as a vertical swipe if vertical movement dominates
      if (Math.abs(dy) > 50 && Math.abs(dy) > dx * 1.5) {
        navigate(dy > 0 ? 1 : -1);
      }
    }, { passive: true });

    // ----------------------------------------------------------------
    // Add to Cart
    // ----------------------------------------------------------------
    atcBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      addToCart();
    });

    // ================================================================
    // Core functions
    // ================================================================

    function openModal(index) {
      stopCarouselAutoplay();
      currentIndex = index;
      loadVideo(index);
      populateInfo(index);
      modalEl.setAttribute('aria-hidden', 'false');
      modalEl.classList.add('is-open');
      document.body.classList.add('ugc-modal-open');
    }

    function closeModal() {
      // Always restore ATC button in case it was in a loading state
      atcBtn.textContent = atcBtnLabel;
      atcBtn.disabled = false;
      videoEl.pause();
      videoEl.removeAttribute('src');
      videoEl.load();
      if (snippetEl) snippetEl.classList.remove('is-expanded');
      if (snippetToggle) snippetToggle.textContent = 'See more';
      modalEl.setAttribute('aria-hidden', 'true');
      modalEl.classList.remove('is-open');
      document.body.classList.remove('ugc-modal-open');
      var rect = carouselEl.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        startCarouselAutoplay();
      }
    }

    // ---- Video loading ----

    function loadVideo(index) {
      var src = videos[index] && videos[index].video;
      if (!src) return;

      // Start unmuted — user has already tapped so browser should permit it.
      // If the browser still blocks unmuted autoplay, fall back to muted.
      isMuted = false;
      videoEl.muted = false;
      muteBtn.classList.remove('is-muted');

      videoEl.src = src;
      videoEl.load();
      videoEl.play().catch(function () {
        isMuted = true;
        videoEl.muted = true;
        muteBtn.classList.add('is-muted');
        videoEl.play().catch(function () {
          // Muted autoplay also blocked — video stays paused, user can tap to play
        });
      });
    }

    function toggleMute() {
      isMuted = !isMuted;
      videoEl.muted = isMuted;
      muteBtn.classList.toggle('is-muted', isMuted);
    }

    function togglePlayPause() {
      if (videoEl.paused) {
        videoEl.play().then(function () {
          flashIcon('show-pause');
        }).catch(function () {});
      } else {
        videoEl.pause();
        flashIcon('show-play');
      }
    }

    function flashIcon(cls) {
      clearTimeout(playPauseTimer);
      playPauseEl.classList.remove('show-play', 'show-pause', 'is-visible');
      void playPauseEl.offsetWidth; // force reflow to restart transition
      playPauseEl.classList.add(cls, 'is-visible');
      playPauseTimer = setTimeout(function () {
        playPauseEl.classList.remove('is-visible');
      }, 700);
    }

    // ---- Navigation ----

    function navigate(dir) {
      var next = currentIndex + dir;
      if (next < 0 || next >= videos.length) return;
      currentIndex = next;
      loadVideo(currentIndex);
      populateInfo(currentIndex);
    }

    // ---- Reviewer info ----

    function populateInfo(index) {
      var data = videos[index] || {};
      var hasReviewer = false;

      // Reviewer name
      if (data.reviewer_name) {
        nameEl.textContent = data.reviewer_name;
        nameEl.style.display = '';
        hasReviewer = true;
      } else {
        nameEl.style.display = 'none';
      }

      // Social handle
      if (data.reviewer_handle) {
        handleEl.textContent = data.reviewer_handle;
        handleEl.style.display = '';
        hasReviewer = true;
      } else {
        handleEl.style.display = 'none';
      }

      // Star rating (1–5)
      if (data.star_rating && data.star_rating >= 1) {
        var rating = Math.min(5, Math.max(1, Math.round(Number(data.star_rating))));
        var stars = '';
        for (var i = 1; i <= 5; i++) {
          stars += i <= rating ? '★' : '☆';
        }
        starsEl.textContent = stars;
        starsEl.setAttribute('aria-label', rating + ' out of 5 stars');
        starsEl.style.display = '';
        hasReviewer = true;
      } else {
        starsEl.style.display = 'none';
      }

      // Snippet
      if (snippetWrapEl && snippetEl && snippetToggle) {
        if (data.snippet) {
          snippetEl.textContent = data.snippet;
          snippetEl.classList.remove('is-expanded');
          snippetToggle.textContent = 'See more';
          snippetWrapEl.style.display = '';
          hasReviewer = true;
        } else {
          snippetWrapEl.style.display = 'none';
        }
      }

      // Hide entire reviewer section if nothing is populated
      reviewerEl.style.display = hasReviewer ? '' : 'none';

      // Product bar — populated dynamically in section mode only
      if (dynProductBar) {
        if (data.product_variant_id) {
          if (dynProductLink)  { dynProductLink.href = data.product_url || '#'; }
          if (dynProductImg)   { dynProductImg.src = data.product_image || ''; dynProductImg.alt = data.product_title || ''; }
          if (dynProductName)  { dynProductName.textContent = data.product_title || ''; }
          if (dynProductPrice) { dynProductPrice.innerHTML  = data.product_price_html || ''; }
          dynProductBar.style.display = '';
        } else {
          dynProductBar.style.display = 'none';
        }
      }

      // Navigation arrows — hide at boundaries
      prevBtn.classList.toggle('is-hidden', index <= 0);
      nextBtn.classList.toggle('is-hidden', index >= videos.length - 1);
    }

    // ---- Add to Cart ----

    function getVariantId() {
      var selectors = (opts.variantInputSelector || '').split(',');
      for (var i = 0; i < selectors.length; i++) {
        var s = selectors[i].trim();
        if (!s) continue;
        var el = document.querySelector(s);
        if (el && el.value) return el.value;
      }
      // Per-video fallback in section mode
      if (opts.dynamicProductBar) {
        var vid = videos[currentIndex];
        if (vid && vid.product_variant_id) return vid.product_variant_id;
      }
      return opts.fallbackVariantId;
    }

    function addToCart() {
      // Use the real PDP form so that line item properties (Size, selling_plan,
      // _is_subscription, etc.) are identical to the standard ATC button.
      // Shopify creates a separate cart row when properties differ, so we must
      // match them exactly to increment quantity rather than duplicate the row.
      var selectors = (opts.variantInputSelector || '').split(',');
      var form = null;
      for (var i = 0; i < selectors.length; i++) {
        var s = selectors[i].trim();
        if (!s) continue;
        var input = document.querySelector(s);
        if (input) { form = input.closest('form'); break; }
      }

      // Show loading state — modal stays open until cart pipeline completes
      atcBtn.textContent = 'Adding…';
      atcBtn.disabled = true;

      // `done` guards against both the interceptor and the safety timer firing
      var done = false;
      var drawerToggle = document.querySelector('#cart-drawer-toggle');

      function onCartReady() {
        if (done) return;
        done = true;
        clearTimeout(safetyTimer);
        // Remove the instance-level property override so the element
        // behaves normally again
        if (drawerToggle) {
          try { delete drawerToggle.checked; } catch (e) {}
        }
        closeModal();
      }

      // Safety valve: close the modal after 5 s even if the cart pipeline
      // never signals completion (network error, drawer element missing, etc.)
      var safetyTimer = setTimeout(onCartReady, 5000);

      // Intercept the moment openCartDrawer sets #cart-drawer-toggle.checked = true.
      // That assignment is the theme's sole signal that the cart mutation is done
      // and the drawer is about to open — the perfect time to close the modal.
      if (drawerToggle) {
        var proto = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked');
        Object.defineProperty(drawerToggle, 'checked', {
          configurable: true,
          get: function () { return proto.get.call(this); },
          set: function (val) {
            proto.set.call(this, val);
            if (val) onCartReady();
          }
        });
      }

      if (form && window.muradCartActions && typeof window.muradCartActions.cartAddFormLegacy === 'function') {
        // PDP mode: use the real form so line-item properties match and Shopify merges rows
        var fd = new FormData(form);
        fd.set('quantity', '1');
        window.muradCartActions.cartAddFormLegacy(fd);
      } else {
        // Section mode (no PDP form): check the live cart first.
        // Shopify treats items with different line-item properties as separate rows, so
        // we must increment the existing row by its key rather than re-adding the variant.
        var variantId = getVariantId();
        if (!variantId) { onCartReady(); return; }
        var vid = parseInt(variantId, 10);
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            var matched = null;
            for (var i = 0; i < cart.items.length; i++) {
              if (cart.items[i].variant_id === vid) { matched = cart.items[i]; break; }
            }
            if (matched) {
              // Increment via line-item key — preserves all existing properties so no duplicate row
              fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: matched.key, quantity: matched.quantity + 1 })
              }).then(function() {
                // cartRefresh fires CART_MUTATION_COMPLETE → openCartDrawer sets
                // #cart-drawer-toggle.checked → our interceptor closes the modal
                if (window.muradCartActions && typeof window.muradCartActions.cartRefresh === 'function') {
                  window.muradCartActions.cartRefresh();
                } else {
                  onCartReady();
                }
              }).catch(onCartReady);
            } else {
              // Not in cart yet — add via the theme's own cart API so the drawer opens normally
              window.muradCartActions.cartAddItems([{ id: vid, quantity: 1 }]);
            }
          })
          .catch(function() {
            window.muradCartActions.cartAddItems([{ id: vid, quantity: 1 }]);
          });
      }
    }
    // ---- Carousel autoplay ----

    function playCarouselThumb(index) {
      if (!autoplayRunning || isHovering) return;
      autoplayIndex = index;

      carouselThumbVideos.forEach(function (v, i) {
        if (i !== index) { v.pause(); v.currentTime = 0; }
      });

      var thumbVid = carouselThumbVideos[index];
      if (!thumbVid) return;

      thumbVid.currentTime = 0;
      thumbVid.play().catch(function () {});

      // Scroll the carousel track horizontally to keep the active thumbnail visible
      if (carouselTrack) {
        var btn = carouselEl.querySelectorAll('[data-ugc-index]')[index];
        if (btn) {
          var scrollEl = btn.closest('.ugc-carousel__item-wrap') || btn;
          var trackRect = carouselTrack.getBoundingClientRect();
          var elRect = scrollEl.getBoundingClientRect();
          carouselTrack.scrollTo({
            left: carouselTrack.scrollLeft + (elRect.left - trackRect.left) - initialTrackPad,
            behavior: 'smooth'
          });
        }
      }

      clearTimeout(autoplayTimer);
      autoplayTimer = setTimeout(function () {
        if (!autoplayRunning) return;
        playCarouselThumb((index + 1) % carouselThumbVideos.length);
      }, 5000);
    }

    function startCarouselAutoplay() {
      if (!carouselThumbVideos.length) return;
      autoplayRunning = true;
      playCarouselThumb(autoplayIndex);
    }

    function stopCarouselAutoplay() {
      autoplayRunning = false;
      isHovering = false;
      clearTimeout(autoplayTimer);
      carouselThumbVideos.forEach(function (v) {
        v.pause();
        v.currentTime = 0;
      });
    }

    // Start autoplay when the carousel scrolls into view; pause when it leaves
    if (typeof IntersectionObserver !== 'undefined' && carouselThumbVideos.length) {
      var carouselObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !autoplayRunning) {
            startCarouselAutoplay();
          } else if (!entry.isIntersecting && autoplayRunning) {
            stopCarouselAutoplay();
          }
        });
      }, { threshold: 0.2 });
      carouselObserver.observe(carouselEl);
    }
  }

  return { init: init };

})();
